/**
 * InfinitSales - Account Page
 * Subscription management: usage, billing, plan details
 *
 * Inspirada em: infinitagents/frontend/src/pages/user/UserAccount.js
 * Estilo: InfinitSales dark theme com useTheme()
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Activity,
  CreditCard,
  Loader2,
  Sparkles,
  Shield,
  AlertCircle,
  ExternalLink,
  ArrowUp,
  ChevronRight,
  Check,
  X,
  Clock,
  LogOut,
  User,
  Key,
  Copy,
  Trash2,
  Plus,
  BookOpen,
  Eye,
  EyeOff,
  Building2,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionService from '../../services/global/subscriptionService';
import apiKeyService from '../../services/global/apiKeyService';
import companyService from '../../services/global/companyService';
import authService from '../../services/global/authService';
import UpgradeModal from '../../components/UpgradeModal';
import ChangePasswordCard from '../../components/account/ChangePasswordCard';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import { formatBRL, seatsBreakdown } from '../../utils/seats';

const AccountPage = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { user, logout, isCompanyAdmin, isCompanyMember, refreshUser } = useAuth();

  const [activeTab, setActiveTab] = useState('usage');
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Conta Empresa promotion state (used in billing tab)
  const [showPromoteForm, setShowPromoteForm] = useState(false);
  const [promoteName, setPromoteName] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [companyError, setCompanyError] = useState('');
  const [upgradeModal, setUpgradeModal] = useState({ open: false, code: null, message: '' });
  const [companyLimits, setCompanyLimits] = useState(null);
  // Cadeiras da Conta Empresa (flag company_seats_billing, 2026-10-01).
  const { flags: featureFlags } = useFeatureFlags();
  // Cobranca de cadeira no cartao (so com company_seats_billing).
  const seatsBilling = !!featureFlags.company_seats_billing;
  // Empresa em qualquer plano (company_any_plan, 14/10): "X de Y usuarios do
  // plano" aparece mesmo sem a cobranca; o backend devolve seats nos dois casos.
  const anyPlan = seatsBilling || !!featureFlags.company_any_plan;
  const [companySeats, setCompanySeats] = useState(null);
  const [activating, setActivating] = useState(false);

  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  // Escrita desmarcada por padrao, de proposito: a chave que o cliente cola
  // no Claude Code so deve poder alterar o CRM se ele tiver marcado isso.
  const [keyCanWrite, setKeyCanWrite] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [keysLoading, setKeysLoading] = useState(false);
  const [showFullKey, setShowFullKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Member never has access to billing/integrations — force-redirect any
    // attempt to land there to the usage tab.
    if (isCompanyMember && (params.get('tab') === 'billing' || activeTab !== 'usage')) {
      setActiveTab('usage');
    }

    // Auto-switch to billing tab when arriving from notification/post-upgrade
    if (!isCompanyMember && params.get('tab') === 'billing') {
      setActiveTab('billing');
    }

    // Returning from Stripe checkout
    const fromCheckout = params.get('success') === 'true';
    if (fromCheckout) {
      setSuccessMessage('Assinatura atualizada com sucesso!');
    }

    // Auto-open the "Virar empresa" inline form when:
    //  - explicitly requested by `?open_company_form=1` (link from inbox notification)
    //  - OR post-checkout success AND tier upgraded to Business AND user has no company
    // The second branch fires after loadData resolves because we need to know the tier.
    if (params.get('open_company_form') === '1' && !user?.company_id) {
      setActiveTab('billing');
      setShowPromoteForm(true);
    }

    // Clean URL params so back/forward doesn't reopen the form
    if (params.toString()) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Pós-checkout: força refetch do status (fura o cache de 60s do service
    // para o tier novo aparecer já na volta do Stripe).
    loadData(fromCheckout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Post-upgrade: when the subscription loads and we see tier=business but no
  // company yet, AND we just came back from a successful checkout, auto-open
  // the promote form. This catches the "user just upgraded their plan" flow
  // without requiring an explicit URL param.
  useEffect(() => {
    if (!subscription) return;
    if (user?.company_id) return; // already in a company
    if (!successMessage) return;  // only after a successful checkout
    const tier = (subscription.tier || '').toLowerCase();
    if (tier.includes('business')) {
      setActiveTab('billing');
      setShowPromoteForm(true);
    }
  }, [subscription, successMessage, user]);

  const loadData = async (forceStatus = false) => {
    setLoading(true);
    setError('');
    try {
      const [res, lim] = await Promise.all([
        subscriptionService.getSubscriptionStatus({ force: forceStatus }),
        companyService.getLimits().catch(() => null),
      ]);
      setSubscription(res.data || res);
      setCompanyLimits(lim);
      if (isCompanyAdmin && user?.company_id) {
        companyService.getSeats(user.company_id)
          .then((s) => setCompanySeats(s))
          .catch(() => setCompanySeats(null));
      }
    } catch (err) {
      console.error('[AccountPage] Error loading data:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    setCompanyError('');
    if (!promoteName.trim()) {
      setCompanyError('Informe o nome da empresa.');
      return;
    }
    setPromoting(true);
    try {
      try {
        await companyService.promote(promoteName.trim());
      } catch (err) {
        // Vinculo com a InfinitAgents sem chave (caso Bigens, 14/10): tenta
        // emitir a chave pelo retry e cria a empresa de novo, uma vez.
        const parsed = companyService.parseCompanyError(err);
        if (parsed.code !== 'infinitagents_not_ready') throw err;
        const retry = await authService.retryInfinitAgentsIntegration().catch(() => null);
        if (!retry?.data?.has_api_key) throw err;
        await companyService.promote(promoteName.trim());
      }
      setShowPromoteForm(false);
      // Recarrega user pra que o AuthContext receba company_id/role atualizados
      await refreshUser?.();
      navigate('/empresa');
    } catch (err) {
      const parsed = companyService.parseCompanyError(err);
      if (parsed.code === 'plan_upgrade_required') {
        setShowPromoteForm(false);
        setUpgradeModal({ open: true, code: parsed.code, message: parsed.message });
      } else {
        setCompanyError(parsed.message);
      }
    } finally {
      setPromoting(false);
    }
  };

  // Plano por concessão (sem cartão): abre o checkout do MESMO plano, com as
  // cadeiras adicionais que a empresa já usa além do incluso e da cortesia.
  const handleActivateBilling = async () => {
    setActivating(true);
    setError('');
    try {
      const res = await subscriptionService.getCheckoutUrl(subscription?.tier);
      const url = res?.data?.checkout_url || res?.checkout_url;
      if (url) {
        window.location.href = url;
      } else {
        setError('Não foi possível abrir o pagamento. Tente de novo.');
      }
    } catch (err) {
      const detalhe = err?.response?.data?.detail;
      setError((detalhe && detalhe.message) || 'Não foi possível abrir o pagamento. Tente de novo.');
    } finally {
      setActivating(false);
    }
  };

  const handleManageStripe = async () => {
    setProcessing(true);
    try {
      const res = await subscriptionService.getPortalUrl();
      const portalUrl = res.data?.portal_url || res.portal_url;
      if (portalUrl) {
        window.location.href = portalUrl;
      } else {
        setError('Erro ao acessar portal de assinatura.');
      }
    } catch (err) {
      console.error('[AccountPage] Error getting portal URL:', err);
      setError('Erro ao acessar portal. Tente novamente.');
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // API Keys handlers
  const loadApiKeys = async () => {
    setKeysLoading(true);
    try {
      const res = await apiKeyService.list();
      setApiKeys(res.data || []);
    } catch (err) {
      console.error('[AccountPage] Error loading API keys:', err);
    } finally {
      setKeysLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;
    setProcessing(true);
    try {
      const res = await apiKeyService.create(
        newKeyName.trim(),
        keyCanWrite
          ? ['crm.read', 'crm.write', 'config.read', 'config.write', 'analytics.read', 'messaging.send']
          : null,
      );
      const keyData = res.data || res;
      setCreatedKey(keyData);
      setShowKeyModal(true);
      setShowFullKey(false);
      setNewKeyName('');
      setKeyCanWrite(false);
      await loadApiKeys();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao criar chave de API';
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await apiKeyService.revoke(keyId);
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      setSuccessMessage('Chave revogada com sucesso');
    } catch (err) {
      setError('Erro ao revogar chave');
    }
  };

  const handleCopyKey = (text) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Copiado!');
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2500);
  };

  // Load API keys when switching to integrations tab
  useEffect(() => {
    if (activeTab === 'integrations') {
      loadApiKeys();
    }
  }, [activeTab]);

  // ============================================
  // STYLES
  // ============================================
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 24px',
      backgroundColor: colors.bgPrimary,
      boxShadow: colors.shadowSm,
      minHeight: '60px',
      zIndex: 1,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    backBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: colors.textPrimary,
      borderRadius: '8px',
      cursor: 'pointer',
    },
    headerIcon: {
      width: '40px',
      height: '40px',
      backgroundColor: colors.accentPrimary,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '24px',
    },
    layout: {
      display: 'flex',
      gap: '24px',
      flexWrap: 'wrap',
    },
    sidebar: {
      width: '240px',
      flexShrink: 0,
    },
    main: {
      flex: 1,
      minWidth: '300px',
    },
    card: {
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '14px',
      overflow: 'hidden',
    },
    sidebarCard: {
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '14px',
      position: 'sticky',
      top: '24px',
    },
    profileHeader: {
      padding: '20px',
      borderBottom: `1px solid ${colors.borderColor}`,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    avatar: {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      backgroundColor: colors.bgTertiary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    tabBtn: (isActive) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
      padding: '12px 16px',
      border: 'none',
      backgroundColor: isActive ? `${colors.accentPrimary}20` : 'transparent',
      color: isActive ? colors.accentPrimary : colors.textSecondary,
      fontSize: '14px',
      fontWeight: isActive ? 600 : 400,
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background-color 0.15s',
      borderLeft: isActive ? `3px solid ${colors.accentPrimary}` : '3px solid transparent',
    }),
    sectionTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: colors.textPrimary,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px',
    },
    // Usage bars
    usageCard: {
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '14px',
      padding: '20px',
    },
    progressBar: (percentage, color) => ({
      width: '100%',
      height: '6px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '3px',
      overflow: 'hidden',
      position: 'relative',
    }),
    progressFill: (percentage, barColor) => ({
      height: '100%',
      width: `${Math.min(percentage, 100)}%`,
      backgroundColor: barColor || colors.accentPrimary,
      borderRadius: '3px',
      transition: 'width 0.5s ease',
    }),
    actionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: '14px 16px',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '10px',
      backgroundColor: 'transparent',
      color: colors.textPrimary,
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background-color 0.15s',
    },
  };

  // ============================================
  // LOADING
  // ============================================
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
          </button>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>Minha Conta</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px', color: colors.textSecondary }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <span>Carregando...</span>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ============================================
  // DATA EXTRACTION
  // ============================================
  const tier = subscription?.tier || 'sales_free';
  const rawStatus = subscription?.status || 'active';
  // Normalize status: "not_integrated", "error", "timeout" all mean the account is being set up
  const isIntegrating = ['not_integrated', 'error', 'timeout'].includes(rawStatus);
  const status = isIntegrating ? 'active' : rawStatus;
  const hasAccess = subscription?.has_access ?? false;
  const limits = subscription?.limits || {};
  const tokensAvailable = subscription?.tokens_available || {};
  const period = subscription?.period || {};

  // Calculate usage - messages_used comes directly from backend (0 = never used)
  const inputTokensUsed = Math.max(0, (limits.input_tokens || 0) - (tokensAvailable.input_tokens || 0));
  const outputTokensUsed = Math.max(0, (limits.output_tokens || 0) - (tokensAvailable.output_tokens || 0));
  const messagesUsed = subscription?.messages_used ?? 0;
  const messagesLimit = subscription?.messages_limit || limits.platform_messages || 0;

  // Unified "Creditos IA" = input_tokens + output_tokens
  const totalCreditsUsed = inputTokensUsed + outputTokensUsed;
  const totalCreditsLimit = (limits.input_tokens || 0) + (limits.output_tokens || 0);
  const creditsPct = totalCreditsLimit ? Math.min(100, Math.round((totalCreditsUsed / totalCreditsLimit) * 100)) : 0;
  const messagesPct = messagesLimit ? Math.min(100, Math.round((messagesUsed / messagesLimit) * 100)) : 0;

  const getBarColor = (pct) => {
    if (pct >= 95) return '#ef4444';
    if (pct >= 80) return '#f59e0b';
    return colors.accentPrimary;
  };

  // Display-friendly plan name
  const planDisplayName = subscription?.plan_name
    || tier.replace('sales_', '').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Plano pago sem assinatura no cartão (concessão): "Ativar cobrança".
  // has_stripe_subscription só vem da InfinitAgents nova; ausente = não mostra.
  const salesProduct = subscription?.products?.sales || null;
  const isManualPlan = seatsBilling && hasAccess && tier !== 'sales_free'
    && !!salesProduct && salesProduct.has_stripe_subscription === false;
  const includedSeats = subscription?.seats?.included ?? limits.included_seats ?? null;
  const seatPrice = formatBRL(
    subscription?.seats?.extra_seat_price_brl ?? companyLimits?.extra_seat_price_brl ?? 89.9,
  );

  // ============================================
  // RENDER
  // ============================================
  return (
    <div style={styles.container}>
      {/* Success Message */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          backgroundColor: colors.accentPrimary,
          color: 'white',
          padding: '12px 20px',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: 500,
        }}>
          <Check size={16} />
          {successMessage}
          <button onClick={() => setSuccessMessage('')} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '2px' }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button style={styles.backBtn} onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <div style={styles.headerIcon}>
            <User size={20} />
          </div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>Minha Conta</h2>
            <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>Gerencie sua assinatura e consumo</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            color: '#ef4444',
            fontSize: '14px',
            marginBottom: '20px',
          }}>
            {error}
          </div>
        )}

        <div style={styles.layout}>
          {/* Sidebar */}
          <div style={styles.sidebar}>
            <div style={styles.sidebarCard}>
              {/* Profile */}
              <div style={styles.profileHeader}>
                <div style={styles.avatar}>
                  <User size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.name || 'Usuario'}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.email}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    marginTop: '6px',
                    padding: '2px 10px',
                    backgroundColor: `${colors.accentPrimary}15`,
                    color: colors.accentPrimary,
                    fontSize: '11px',
                    fontWeight: 600,
                    borderRadius: '12px',
                  }}>
                    {planDisplayName}
                  </div>
                </div>
              </div>

              {/* Tabs — Assinatura e Integrações ficam escondidos para
                  Conta Empresa members (admin gerencia tudo). */}
              <div style={{ padding: '8px 0' }}>
                <button style={styles.tabBtn(activeTab === 'usage')} onClick={() => setActiveTab('usage')}>
                  <Activity size={16} />
                  Consumo
                </button>
                {!isCompanyMember && (
                  <button style={styles.tabBtn(activeTab === 'billing')} onClick={() => setActiveTab('billing')}>
                    <CreditCard size={16} />
                    Assinatura
                  </button>
                )}
                {!isCompanyMember && (
                  <button style={styles.tabBtn(activeTab === 'integrations')} onClick={() => setActiveTab('integrations')}>
                    <Key size={16} />
                    Integrações
                  </button>
                )}
                {/* Central de Ajuda: absorveu a antiga pagina de tutoriais
                    (/tutorials redireciona para dentro dela). */}
                <button style={styles.tabBtn(false)} onClick={() => navigate('/ajuda')}>
                  <BookOpen size={16} />
                  Central de Ajuda
                </button>
              </div>

              {/* Logout */}
              <div style={{ padding: '8px 12px 12px', borderTop: `1px solid ${colors.borderColor}` }}>
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 16px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#ef4444',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderRadius: '8px',
                    transition: 'background-color 0.15s',
                  }}
                >
                  <LogOut size={16} />
                  Sair da conta
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div style={styles.main}>

            {/* ====== USAGE TAB ====== */}
            {activeTab === 'usage' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Conta Empresa: card informativo para members */}
                {isCompanyMember && (
                  <div style={{
                    backgroundColor: colors.bgPrimary,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '14px',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: `${colors.accentPrimary}20`,
                      color: colors.accentPrimary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Building2 size={20} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary, marginBottom: '4px' }}>
                        Você é membro de uma Conta Empresa
                      </div>
                      <div style={{ fontSize: '13px', color: colors.textSecondary, lineHeight: 1.55 }}>
                        Plano, agente IA e configurações de cobrança são gerenciados pelo administrador da empresa.
                        Você pode trocar sua senha normalmente, mas alterações na assinatura precisam ser solicitadas
                        ao admin.
                      </div>
                    </div>
                  </div>
                )}

                {/* Plan Status Banner */}
                <div style={{
                  backgroundColor: colors.bgPrimary,
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: '14px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Sparkles size={14} color={colors.textSecondary} />
                      <span style={{ fontSize: '12px', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Plano Atual</span>
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}>{planDisplayName}</div>
                    {period.end && (
                      <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '4px' }}>
                        {status === 'canceled' ? 'Termina em: ' : 'Renova em: '}
                        {new Date(period.end).toLocaleDateString('pt-BR')}
                      </div>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: status === 'active' ? colors.accentPrimary : '#ef4444',
                    }} />
                    <span style={{ fontSize: '13px', color: colors.textSecondary }}>
                      {status === 'active' ? 'Ativo' : status === 'canceled' ? 'Cancelado' : status}
                    </span>
                  </div>
                </div>

                {/* Usage Bars */}
                {totalCreditsLimit > 0 && (
                  <div style={styles.usageCard}>
                    <div style={styles.sectionTitle}>
                      <Shield size={14} color={colors.textSecondary} />
                      Consumo do Ciclo
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {/* Creditos IA (unified input + output tokens) */}
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ fontSize: '13px', color: colors.textSecondary }}>Créditos IA</span>
                          <span style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>
                            {subscriptionService.formatTokens(totalCreditsUsed)} / {subscriptionService.formatTokens(totalCreditsLimit)}
                          </span>
                        </div>
                        <div style={styles.progressBar(creditsPct)}>
                          <div style={styles.progressFill(creditsPct, getBarColor(creditsPct))} />
                        </div>
                      </div>

                      {/* Messages */}
                      {messagesLimit > 0 && (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '13px', color: colors.textSecondary }}>Mensagens</span>
                            <span style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>
                              {subscriptionService.formatTokens(messagesUsed)} / {subscriptionService.formatTokens(messagesLimit)}
                            </span>
                          </div>
                          <div style={styles.progressBar(messagesPct)}>
                            <div style={styles.progressFill(messagesPct, getBarColor(messagesPct))} />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Warning */}
                    {(creditsPct > 80 || messagesPct > 80) && (
                      <div style={{
                        marginTop: '16px',
                        padding: '12px 16px',
                        backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        border: '1px solid rgba(245, 158, 11, 0.25)',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}>
                        <AlertCircle size={16} color="#f59e0b" />
                        <span style={{ fontSize: '13px', color: '#f59e0b', flex: 1 }}>
                          Você está usando mais de 80% dos recursos. Considere fazer upgrade.
                        </span>
                        <button
                          onClick={() => navigate('/plans')}
                          style={{
                            padding: '6px 14px',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Ver planos
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* No subscription */}
                {!hasAccess && tier === 'sales_free' && (
                  <div style={{
                    ...styles.usageCard,
                    textAlign: 'center',
                    padding: '40px 20px',
                  }}>
                    <Sparkles size={32} color={colors.accentPrimary} style={{ marginBottom: '12px' }} />
                    <div style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, marginBottom: '8px' }}>
                      Voce esta no plano gratuito
                    </div>
                    <div style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '20px' }}>
                      Faca upgrade para desbloquear mais recursos e mensagens
                    </div>
                    <button
                      onClick={() => navigate('/plans')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 24px',
                        backgroundColor: colors.accentPrimary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Ver planos
                      <ArrowUp size={16} />
                    </button>
                  </div>
                )}

                {/* Trocar senha (2026-10-14): vale para todos, inclusive
                    membros de Conta Empresa (o aviso acima promete isso). */}
                <ChangePasswordCard colors={colors} cardStyle={styles.usageCard} titleStyle={styles.sectionTitle} />
              </div>
            )}

            {/* ====== BILLING TAB ====== */}
            {activeTab === 'billing' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Plan Details */}
                <div style={styles.usageCard}>
                  <div style={styles.sectionTitle}>
                    <CreditCard size={14} color={colors.textSecondary} />
                    Sua Assinatura
                  </div>

                  {/* Current Plan */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: colors.bgTertiary,
                    borderRadius: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>{planDisplayName}</div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}>
                        {isManualPlan ? 'Concessão, sem cartão cadastrado' : 'Cobrado mensalmente'}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: status === 'active' ? colors.accentPrimary : '#ef4444',
                      }} />
                      <span style={{ fontSize: '13px', color: colors.textSecondary }}>
                        {status === 'active' ? 'Ativo' : status === 'canceled' ? 'Cancelado' : status}
                      </span>
                    </div>
                  </div>

                  {/* Period */}
                  {period.end && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      border: `1px solid ${colors.borderColor}`,
                      borderRadius: '10px',
                      marginBottom: '16px',
                    }}>
                      <Clock size={16} color={colors.textSecondary} />
                      <span style={{ fontSize: '13px', color: colors.textSecondary }}>
                        {status === 'canceled' ? 'Acesso até: ' : isManualPlan ? 'Válido até: ' : 'Próxima renovação: '}
                        <span style={{ color: colors.textPrimary, fontWeight: 500 }}>
                          {new Date(period.end).toLocaleDateString('pt-BR')}
                        </span>
                      </span>
                    </div>
                  )}

                  {isManualPlan && (
                    <div style={{
                      padding: '14px 16px',
                      border: `1px solid ${colors.accentPrimary}`,
                      borderRadius: '10px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: '12px',
                    }}>
                      <div style={{ flex: '1 1 240px', fontSize: '13px', color: colors.textSecondary, lineHeight: 1.5 }}>
                        Cadastre o cartão para a renovação do plano {planDisplayName} ficar automática.
                        A primeira cobrança acontece na confirmação.
                      </div>
                      <button
                        onClick={handleActivateBilling}
                        disabled={activating}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '10px 16px', border: 'none', borderRadius: '10px',
                          backgroundColor: colors.accentPrimary, color: 'white',
                          fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        {activating
                          ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          : <CreditCard size={14} />}
                        Ativar cobrança no cartão
                      </button>
                    </div>
                  )}
                </div>

                {isCompanyAdmin && companySeats && (
                  <div style={styles.usageCard}>
                    <div style={styles.sectionTitle}>
                      <Building2 size={14} color={colors.textSecondary} />
                      Usuários da empresa
                    </div>
                    <div style={{ fontSize: '14px', color: colors.textPrimary, marginBottom: '4px' }}>
                      {companySeats.used} de {companySeats.total} usuários
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5, marginBottom: '12px' }}>
                      {seatsBreakdown(companySeats)}.
                      {companySeats.billing_enabled === false
                        ? ' Para mais usuários, escolha um plano com mais usuários incluídos.'
                        : ` Usuário adicional: ${seatPrice} por mês, na mesma fatura.`}
                    </div>
                    <button style={styles.actionBtn} onClick={() => navigate('/empresa')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Building2 size={16} color={colors.textSecondary} />
                        <span>Gerenciar membros</span>
                      </div>
                      <ChevronRight size={16} color={colors.textSecondary} />
                    </button>
                  </div>
                )}

                {/* Actions */}
                <div style={styles.usageCard}>
                  <div style={styles.sectionTitle}>Gerenciar</div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button
                      style={styles.actionBtn}
                      onClick={() => navigate('/plans')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <ArrowUp size={16} color={colors.textSecondary} />
                        <span>Alterar plano</span>
                      </div>
                      <ChevronRight size={16} color={colors.textSecondary} />
                    </button>

                    <button
                      style={styles.actionBtn}
                      onClick={handleManageStripe}
                      disabled={processing}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {processing ? (
                          <Loader2 size={16} color={colors.textSecondary} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <ExternalLink size={16} color={colors.textSecondary} />
                        )}
                        <span>Gerenciar no Stripe</span>
                      </div>
                      <span style={{
                        fontSize: '11px',
                        backgroundColor: colors.bgTertiary,
                        color: colors.textSecondary,
                        padding: '3px 10px',
                        borderRadius: '12px',
                      }}>
                        Faturas, cartao, cancelar
                      </span>
                    </button>
                  </div>

                  <div style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    backgroundColor: colors.bgTertiary,
                    borderRadius: '10px',
                  }}>
                    <p style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5 }}>
                      No portal do Stripe você pode alterar forma de pagamento, ver histórico de faturas e cancelar assinatura.
                    </p>
                  </div>
                </div>

                {/* ===== Conta Empresa ===== */}
                <div style={styles.usageCard}>
                  <div style={styles.sectionTitle}>
                    <Building2 size={14} color={colors.textSecondary} />
                    Conta Empresa
                  </div>

                  {isCompanyAdmin ? (
                    <>
                      <p style={{ fontSize: '13px', color: colors.textSecondary, lineHeight: 1.5, marginBottom: '12px' }}>
                        Você é o administrador de uma empresa. Gerencie membros e configurações.
                      </p>
                      <button
                        style={styles.actionBtn}
                        onClick={() => navigate('/empresa')}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Building2 size={16} color={colors.textSecondary} />
                          <span>Abrir painel da empresa</span>
                        </div>
                        <ChevronRight size={16} color={colors.textSecondary} />
                      </button>
                    </>
                  ) : isCompanyMember ? (
                    <p style={{ fontSize: '13px', color: colors.textSecondary, lineHeight: 1.5 }}>
                      Você está vinculado a uma Conta Empresa. As configurações de plano e
                      faturamento são gerenciadas pelo administrador da empresa.
                    </p>
                  ) : showPromoteForm ? (
                    <div>
                      <p style={{ fontSize: '13px', color: colors.textSecondary, lineHeight: 1.5, marginBottom: '12px' }}>
                        {anyPlan ? (
                          tier === 'sales_free' ? (
                            <>
                              Defina um nome para sua empresa. O plano gratuito inclui só você; para
                              adicionar membros, assine um plano.
                            </>
                          ) : (
                            <>
                              Defina um nome para sua empresa. Seu plano inclui{' '}
                              <strong>{includedSeats ?? '?'}</strong> usuários (você incluído). Cada
                              usuário adicional custa {seatPrice} por mês.
                            </>
                          )
                        ) : (
                          <>
                            Defina um nome para sua empresa. Você poderá criar até{' '}
                            <strong>{companyLimits?.max_total_users ?? 2}</strong> usuários no total
                            (você + membros). Membros adicionais a R$ 89,90/mês em breve.
                          </>
                        )}
                      </p>
                      <input
                        type="text"
                        value={promoteName}
                        onChange={(e) => setPromoteName(e.target.value)}
                        placeholder="Nome da empresa"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: `1px solid ${colors.borderColor}`,
                          borderRadius: '10px',
                          backgroundColor: colors.bgSecondary,
                          color: colors.textPrimary,
                          fontSize: '14px',
                          outline: 'none',
                          marginBottom: '12px',
                        }}
                        maxLength={120}
                      />
                      {companyError && (
                        <div style={{
                          fontSize: '13px',
                          color: '#ef4444',
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}>
                          <AlertCircle size={14} /> {companyError}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => { setShowPromoteForm(false); setCompanyError(''); }}
                          style={{
                            padding: '10px 16px',
                            border: `1px solid ${colors.borderColor}`,
                            borderRadius: '10px',
                            backgroundColor: 'transparent',
                            color: colors.textPrimary,
                            fontSize: '14px',
                            cursor: 'pointer',
                          }}
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleCreateCompany}
                          disabled={promoting}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '10px 16px',
                            border: 'none',
                            borderRadius: '10px',
                            backgroundColor: colors.accentPrimary,
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {promoting ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Building2 size={14} />}
                          Criar empresa
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: '13px', color: colors.textSecondary, lineHeight: 1.5, marginBottom: '12px' }}>
                        Crie uma Conta Empresa para adicionar membros que compartilham o
                        mesmo agente IA e a mesma assinatura. Cada vendedor conecta o próprio
                        WhatsApp; o consumo da IA cai todo na sua conta.
                      </p>
                      <button
                        style={styles.actionBtn}
                        onClick={() => setShowPromoteForm(true)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Building2 size={16} color={colors.textSecondary} />
                          <span>Virar empresa</span>
                        </div>
                        <span style={{
                          fontSize: '11px',
                          backgroundColor: colors.bgTertiary,
                          color: colors.textSecondary,
                          padding: '3px 10px',
                          borderRadius: '12px',
                        }}>
                          {anyPlan
                            ? (includedSeats
                              ? `${includedSeats} ${includedSeats === 1 ? 'usuário' : 'usuários'} no seu plano`
                              : 'Qualquer plano')
                            : 'Plano Business'}
                        </span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ====== INTEGRATIONS TAB ====== */}
            {activeTab === 'integrations' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Header */}
                <div style={styles.usageCard}>
                  <div style={styles.sectionTitle}>
                    <Key size={14} color={colors.textSecondary} />
                    Chaves de API
                  </div>
                  <p style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '16px', lineHeight: 1.5 }}>
                    Gere chaves de API para integrar o envio de mensagens WhatsApp em seus sistemas externos.
                    Cada chave permite autenticar chamadas à API de integração.
                  </p>

                  {/* Create new key */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="Nome da chave (ex: Meu Site)"
                      maxLength={50}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateKey()}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        backgroundColor: colors.bgTertiary,
                        border: `1px solid ${colors.borderColor}`,
                        borderRadius: '8px',
                        color: colors.textPrimary,
                        fontSize: '14px',
                        outline: 'none',
                      }}
                    />
                    <button
                      onClick={handleCreateKey}
                      disabled={processing || !newKeyName.trim()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        backgroundColor: !newKeyName.trim() ? colors.bgTertiary : colors.accentPrimary,
                        color: !newKeyName.trim() ? colors.textSecondary : 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: newKeyName.trim() ? 'pointer' : 'default',
                        whiteSpace: 'nowrap',
                        opacity: processing ? 0.7 : 1,
                      }}
                    >
                      {processing ? (
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <Plus size={16} />
                      )}
                      Gerar chave
                    </button>
                  </div>

                  {/* Escopo de escrita. Desmarcado por padrao: a chave que so le
                      nao consegue estragar a conta, e a maioria dos usos e de
                      leitura. Quem marca esta pedindo poder de alterar. */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      marginBottom: '20px',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={keyCanWrite}
                      onChange={(e) => setKeyCanWrite(e.target.checked)}
                      style={{ marginTop: '2px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5 }}>
                      <strong style={{ color: colors.textPrimary }}>
                        Esta chave tambem pode alterar o CRM
                      </strong>
                      <br />
                      Sem marcar, a chave so le e envia mensagem. Marcando, ela passa a
                      criar tag, criar campo, mover contato de etapa e comentar. Marque
                      quando for usar a chave para administrar a conta, por exemplo pelo
                      Claude Code.
                    </span>
                  </label>

                  {/* Keys list */}
                  {keysLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px', color: colors.textSecondary }}>
                      <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : apiKeys.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '30px 20px',
                      color: colors.textSecondary,
                      fontSize: '14px',
                    }}>
                      <Key size={28} style={{ marginBottom: '8px', opacity: 0.4 }} />
                      <div>Nenhuma chave de API criada</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {apiKeys.map((key) => (
                        <div
                          key={key.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            backgroundColor: colors.bgTertiary,
                            borderRadius: '10px',
                            gap: '12px',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: colors.textPrimary }}>{key.name}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '12px', color: colors.textSecondary, fontFamily: 'monospace' }}>
                                {key.prefix}...
                              </span>
                              <span style={{ fontSize: '11px', color: colors.textSecondary }}>
                                Criada em {new Date(key.created_at).toLocaleDateString('pt-BR')}
                              </span>
                              {key.last_used_at && (
                                <span style={{ fontSize: '11px', color: colors.textSecondary }}>
                                  Usada {new Date(key.last_used_at).toLocaleDateString('pt-BR')}
                                </span>
                              )}
                              <span style={{
                                fontSize: '11px',
                                backgroundColor: `${colors.accentPrimary}15`,
                                color: colors.accentPrimary,
                                padding: '1px 8px',
                                borderRadius: '10px',
                              }}>
                                {key.usage_count || 0} chamadas
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRevokeKey(key.id)}
                            title="Revogar chave"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '32px',
                              height: '32px',
                              border: 'none',
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              color: '#ef4444',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              flexShrink: 0,
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Documentation link */}
                <div style={styles.usageCard}>
                  <div style={styles.sectionTitle}>
                    <BookOpen size={14} color={colors.textSecondary} />
                    Documentação
                  </div>
                  <p style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '12px', lineHeight: 1.5 }}>
                    Consulte a documentação completa da API de integração para enviar mensagens via WhatsApp.
                  </p>
                  <button
                    style={styles.actionBtn}
                    onClick={() => navigate('/integration-docs')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <BookOpen size={16} color={colors.textSecondary} />
                      <span>Ver documentação da API</span>
                    </div>
                    <ChevronRight size={16} color={colors.textSecondary} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Key Created Modal */}
          {showKeyModal && createdKey && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}>
              <div style={{
                backgroundColor: colors.bgPrimary,
                border: `1px solid ${colors.borderColor}`,
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '520px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      backgroundColor: `${colors.accentPrimary}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Key size={16} color={colors.accentPrimary} />
                    </div>
                    <span style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>Chave criada</span>
                  </div>
                  <button
                    onClick={() => { setShowKeyModal(false); setCreatedKey(null); }}
                    style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer', padding: '4px' }}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div style={{
                  padding: '12px 16px',
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.25)',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <AlertCircle size={16} color="#f59e0b" />
                  <span style={{ fontSize: '13px', color: '#f59e0b' }}>
                    Copie esta chave agora. Ela não será exibida novamente.
                  </span>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: colors.textSecondary }}>Nome: <strong style={{ color: colors.textPrimary }}>{createdKey.name}</strong></span>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  backgroundColor: colors.bgTertiary,
                  borderRadius: '8px',
                  marginBottom: '16px',
                }}>
                  <code style={{
                    flex: 1,
                    fontSize: '13px',
                    color: colors.textPrimary,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    lineHeight: 1.4,
                  }}>
                    {showFullKey ? createdKey.key : createdKey.prefix + '••••••••••••••••••••••'}
                  </code>
                  <button
                    onClick={() => setShowFullKey(!showFullKey)}
                    title={showFullKey ? 'Ocultar' : 'Mostrar'}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.textSecondary,
                      cursor: 'pointer',
                      padding: '4px',
                      flexShrink: 0,
                    }}
                  >
                    {showFullKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    onClick={() => handleCopyKey(createdKey.key)}
                    title={keyCopied ? 'Copiado!' : 'Copiar'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      backgroundColor: keyCopied ? '#25D366' : colors.accentPrimary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'background-color 0.2s',
                    }}
                  >
                    {keyCopied ? <Check size={14} /> : <Copy size={14} />}
                    {keyCopied ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>

                <button
                  onClick={() => { setShowKeyModal(false); setCreatedKey(null); }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '8px',
                    backgroundColor: 'transparent',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      <UpgradeModal
        open={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, code: null, message: '' })}
        code={upgradeModal.code}
        message={upgradeModal.message}
        extraSeatPriceBrl={companyLimits?.extra_seat_price_brl}
      />
    </div>
  );
};

export default AccountPage;
